import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Avatar,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Person,
  AccessTime,
  Assignment,
  TrendingUp,
  TrendingDown,
  Star,
  Group,
  Speed,
  CheckCircle,
  Schedule,
  WorkHistory,
  Psychology,
  EmojiEvents
} from '@mui/icons-material';
import { analyticsService } from '../../../services/analyticsService';

interface DateRange {
  start: Date;
  end: Date;
}

interface TeamProductivityProps {
  dateRange: DateRange;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  avatar: string;
  status: 'active' | 'on-leave' | 'busy' | 'available';
  productivity: {
    score: number;
    trend: 'up' | 'down' | 'stable';
    change: number;
  };
  workload: {
    current: number;
    capacity: number;
    utilization: number;
  };
  timeTracking: {
    totalHours: number;
    billableHours: number;
    efficiency: number;
  };
  tasks: {
    completed: number;
    inProgress: number;
    overdue: number;
    total: number;
  };
  projects: string[];
  performance: {
    quality: number;
    speed: number;
    collaboration: number;
    innovation: number;
  };
  recentAchievements: string[];
}

interface TeamStats {
  totalMembers: number;
  activeMembers: number;
  averageProductivity: number;
  totalBillableHours: number;
  averageUtilization: number;
  completedTasks: number;
  overdueTasks: number;
  teamSatisfaction: number;
}

const TeamProductivity: React.FC<TeamProductivityProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamData, setTeamData] = useState<any>(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [sortBy, setSortBy] = useState('productivity');
  const [viewMode, setViewMode] = useState('cards');

  // Load team productivity data from API
  useEffect(() => {
    const loadTeamProductivity = async () => {
      try {
        setLoading(true);
        setError(null);

        const filters = {
          dateRange: {
            start: dateRange.start.toISOString(),
            end: dateRange.end.toISOString()
          },
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          role: selectedRole !== 'all' ? selectedRole : undefined
        };

        const data = await analyticsService.getTeamProductivity(filters);
        setTeamData(data);
      } catch (err) {
        console.error('Error loading team productivity data:', err);
        setError('Failed to load team productivity data');
      } finally {
        setLoading(false);
      }
    };

    loadTeamProductivity();
  }, [dateRange, selectedDepartment, selectedRole]);

  // Transform API data to component format
  const teamMembers: TeamMember[] = teamData?.team_members?.map((member: any) => ({
    id: member.id,
    name: member.name,
    email: member.email,
    role: member.role,
    department: member.department,
    avatar: member.name.split(' ').map((n: string) => n[0]).join('').toUpperCase(),
    status: member.status || 'active',
    productivity: {
      score: member.productivity_score || 0,
      trend: member.productivity_trend || 'stable',
      change: member.productivity_change || 0
    },
    workload: {
      current: member.current_workload || 0,
      capacity: member.capacity || 40,
      utilization: member.utilization || 0
    },
    timeTracking: {
      totalHours: member.total_hours || 0,
      billableHours: member.billable_hours || 0,
      efficiency: member.efficiency || 0
    },
    tasks: {
      completed: member.tasks_completed || 0,
      inProgress: member.tasks_in_progress || 0,
      overdue: member.tasks_overdue || 0,
      total: member.tasks_total || 0
    },
    projects: member.projects || [],
    performance: {
      quality: member.quality_rating || 0,
      speed: member.speed_rating || 0,
      collaboration: member.collaboration_rating || 0,
      innovation: member.innovation_rating || 0
    },
    recentAchievements: member.recent_achievements || []
  })) || [];

  // Calculate team stats from API data or fallback
  const activeMembers = teamMembers.filter(m => m.status !== 'on-leave');
  const teamStats: TeamStats = teamData?.summary || {
    totalMembers: teamMembers.length,
    activeMembers: activeMembers.length,
    averageProductivity: activeMembers.length > 0 ? 
      activeMembers.reduce((sum, m) => sum + m.productivity.score, 0) / activeMembers.length : 0,
    totalBillableHours: activeMembers.reduce((sum, m) => sum + m.timeTracking.billableHours, 0),
    averageUtilization: activeMembers.length > 0 ?
      activeMembers.reduce((sum, m) => sum + m.workload.utilization, 0) / activeMembers.length : 0,
    completedTasks: activeMembers.reduce((sum, m) => sum + m.tasks.completed, 0),
    overdueTasks: activeMembers.reduce((sum, m) => sum + m.tasks.overdue, 0),
    teamSatisfaction: teamData?.summary?.teamSatisfaction || 4.6
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'busy': return 'warning';
      case 'available': return 'info';
      case 'on-leave': return 'default';
      default: return 'default';
    }
  };

  const getProductivityColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 80) return 'info';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization > 100) return 'error';
    if (utilization > 90) return 'warning';
    if (utilization >= 70) return 'success';
    return 'info';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp color="success" fontSize="small" />;
      case 'down': return <TrendingDown color="error" fontSize="small" />;
      default: return null;
    }
  };

  const departments = Array.from(new Set(teamMembers.map(m => m.department)));
  const roles = Array.from(new Set(teamMembers.map(m => m.role)));

  // Filter and sort team members
  const filteredMembers = teamMembers
    .filter(member => {
      const matchesDepartment = selectedDepartment === 'all' || member.department === selectedDepartment;
      const matchesRole = selectedRole === 'all' || member.role === selectedRole;
      return matchesDepartment && matchesRole;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'productivity':
          return b.productivity.score - a.productivity.score;
        case 'utilization':
          return b.workload.utilization - a.workload.utilization;
        case 'tasks':
          return b.tasks.completed - a.tasks.completed;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Team Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Group color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Team Size</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {teamStats.activeMembers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamStats.totalMembers} total members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Productivity</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {teamStats.averageProductivity.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Out of 100 points
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessTime color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Billable Hours</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {teamStats.totalBillableHours}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Task Completion</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {teamStats.completedTasks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamStats.overdueTasks} overdue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Team Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="productivity">Productivity</MenuItem>
                <MenuItem value="utilization">Utilization</MenuItem>
                <MenuItem value="tasks">Tasks Completed</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>View</InputLabel>
              <Select
                value={viewMode}
                label="View"
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="cards">Card View</MenuItem>
                <MenuItem value="table">Table View</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Team Members Display */}
      {viewMode === 'cards' ? (
        <Grid container spacing={3}>
          {filteredMembers.map((member) => (
            <Grid size={{ xs: 12, md: 6 }} key={member.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  {/* Member Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {member.avatar}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{member.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.role}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={member.status}
                      color={getStatusColor(member.status) as any}
                    />
                  </Box>

                  {/* Productivity Score */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Productivity Score</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" color={`${getProductivityColor(member.productivity.score)}.main`}>
                          {member.productivity.score}
                        </Typography>
                        {getTrendIcon(member.productivity.trend)}
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={member.productivity.score}
                      color={getProductivityColor(member.productivity.score) as any}
                    />
                  </Box>

                  {/* Workload & Utilization */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Workload: {member.workload.current}h / {member.workload.capacity}h
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((member.workload.current / member.workload.capacity) * 100, 100)}
                      color={getUtilizationColor(member.workload.utilization) as any}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {member.workload.utilization}% utilization
                    </Typography>
                  </Box>

                  {/* Performance Metrics */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Quality</Typography>
                      <Rating value={member.performance.quality} readOnly size="small" />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Speed</Typography>
                      <Rating value={member.performance.speed} readOnly size="small" />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Collaboration</Typography>
                      <Rating value={member.performance.collaboration} readOnly size="small" />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="text.secondary">Innovation</Typography>
                      <Rating value={member.performance.innovation} readOnly size="small" />
                    </Grid>
                  </Grid>

                  {/* Tasks Summary */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main">{member.tasks.completed}</Typography>
                      <Typography variant="caption">Completed</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="info.main">{member.tasks.inProgress}</Typography>
                      <Typography variant="caption">In Progress</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="error.main">{member.tasks.overdue}</Typography>
                      <Typography variant="caption">Overdue</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Team Member</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Productivity</TableCell>
                <TableCell>Utilization</TableCell>
                <TableCell>Tasks</TableCell>
                <TableCell>Billable Hours</TableCell>
                <TableCell>Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2 }}>{member.avatar}</Avatar>
                      <Box>
                        <Typography variant="subtitle2">{member.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.role}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      size="small"
                      label={member.status}
                      color={getStatusColor(member.status) as any}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {member.productivity.score}
                      </Typography>
                      {getTrendIcon(member.productivity.trend)}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {member.workload.utilization.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {member.tasks.completed}/{member.tasks.total}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {member.timeTracking.billableHours}h
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip size="small" label={`Q: ${member.performance.quality}`} variant="outlined" />
                      <Chip size="small" label={`S: ${member.performance.speed}`} variant="outlined" />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Team Insights */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performers
            </Typography>
            <List>
              {filteredMembers
                .sort((a, b) => b.productivity.score - a.productivity.score)
                .slice(0, 3)
                .map((member, index) => (
                  <ListItem key={member.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: index === 0 ? 'gold' : index === 1 ? 'silver' : '#cd7f32' }}>
                        <EmojiEvents />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.name}
                      secondary={`Productivity Score: ${member.productivity.score}`}
                    />
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Metrics Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Average Team Satisfaction</Typography>
                <Rating value={teamStats.teamSatisfaction} readOnly />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Workload Distribution</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={teamStats.averageUtilization} 
                  color={getUtilizationColor(teamStats.averageUtilization) as any}
                />
                <Typography variant="caption">{teamStats.averageUtilization.toFixed(1)}% average utilization</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TeamProductivity;